import type {
  BookingDetailPickup,
  CreateBookingDropoffChassis,
  CreateBookingDropoffContainer,
  CreateBookingDropoffTrailer,
  CreateBookingDropoffWheeled,
  CreateBookingRequest,
  CreateBookingSealCapture,
  LookupDetailItem,
} from "api-client";
import { resolveEquipmentPiecesWireLabel, resolveExpiresWhenWireLabel } from "api-client";
import type { BookingCostPayloadFields } from "./booking-wizard-options";
import type {
  PickupStaticEquipment,
  PickupStaticWheeledVariant,
} from "../constants/pickup-static-equipment.constants";
import {
  buildPickupMultiPieceEquipmentDetails,
  composePickupMultipieceEquipmentDetailsLabel,
  findPickupStaticEquipmentFromPickupBranch,
  isPickupMultiPieceWireRowOptionId,
  PICKUP_MULTIPIECE_WIRE_ROW_ID_PREFIX,
} from "../constants/pickup-static-equipment.constants";
import type {
  CreateBookingDropoffStackEquipmentValues,
  CreateBookingDropoffWheeledEquipmentValues,
  CreateBookingPickupEquipmentValues,
  CreateBookingReeferChoice,
  CreateBookingScheduleStepValues,
  CreateBookingSealCaptureFields,
  CreateBookingStepOneValues,
} from "design-components";
import { combineScheduleDateAndTime } from "design-components";

export interface CreateBookingWizardDraft {
  stepOne: CreateBookingStepOneValues;
  scheduledDateTime: Date | null;
  mustArriveBy: Date | null;
  /** AV-186 — date/time parts when schedule or MAB is date-only. */
  scheduledDatePart?: Date | null;
  scheduledTimePart?: Date | null;
  mustArriveDatePart?: Date | null;
  mustArriveTimePart?: Date | null;
  /** IANA timezone when known (customer location); else browser TZ per D-014. */
  locationTimeZone?: string | null;
  pickupEquipment?: CreateBookingPickupEquipmentValues | null;
  dropoffStack?: CreateBookingDropoffStackEquipmentValues | null;
  dropoffWheeled?: CreateBookingDropoffWheeledEquipmentValues | null;
  /** Pickup equipment display label for `equipmentDetails`. */
  pickupEquipmentLabel?: string | null;
  /** Selected static pickup equipment row for structured pickup payload fields. */
  pickupStaticEquipment?: PickupStaticEquipment | null;
  /** Preserved GET `pickup.equipmentDetails` when not in static catalog (AV-44 edit). */
  pickupEquipmentDetails?: string | null;
  /** Full GET pickup branch for edit round-trip when static catalog has no match (AV-44). */
  pickupDetailSnapshot?: BookingDetailPickup | null;
  /** Precomputed day rate — matches capacity/cost drawer (FR-AV131-API-002). */
  costPayload?: BookingCostPayloadFields | null;
  equipmentPiecesLookup?: LookupDetailItem[];
  expiresWhenLookup?: LookupDetailItem[];
}

/** Map schedule step values onto create/update wizard draft schedule fields (FR-AV186-002). */
export function scheduleStepToDraftFields(
  schedule: CreateBookingScheduleStepValues,
): Pick<
  CreateBookingWizardDraft,
  | "scheduledDateTime"
  | "mustArriveBy"
  | "scheduledDatePart"
  | "scheduledTimePart"
  | "mustArriveDatePart"
  | "mustArriveTimePart"
> {
  return {
    scheduledDateTime: schedule.scheduledDateTime,
    mustArriveBy: schedule.mustArriveBy,
    scheduledDatePart: schedule.scheduledDatePart ?? null,
    scheduledTimePart: schedule.scheduledTimePart ?? null,
    mustArriveDatePart: schedule.mustArriveDatePart ?? null,
    mustArriveTimePart: schedule.mustArriveTimePart ?? null,
  };
}

/** Map API abbreviations (e.g. CST) to IANA for `Intl` formatting. */
const TIMEZONE_ABBREV_TO_IANA: Record<string, string> = {
  CTC: "America/Chicago",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  EST: "America/New_York",
  EDT: "America/New_York",
  MST: "America/Denver",
  MDT: "America/Denver",
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  IST: "Asia/Kolkata",
};

export function resolveIanaTimeZone(raw: string | null | undefined): string {
  const trimmed = raw?.trim();
  if (!trimmed) {
    return resolveBrowserTimeZone();
  }
  const mapped = TIMEZONE_ABBREV_TO_IANA[trimmed.toUpperCase()];
  const candidate = mapped ?? trimmed;
  return isValidTimeZone(candidate) ? candidate : resolveBrowserTimeZone();
}

export function resolveCreateBookingTimeZone(draft: CreateBookingWizardDraft): string {
  return resolveIanaTimeZone(draft.locationTimeZone);
}

/**
 * `YYYY-MM-DD` from picker local date parts (FR-NOTZ-002).
 * `_timeZone` retained for call-site compatibility; not used for reformatting.
 */
export function formatBookingDateForApi(date: Date, _timeZone?: string): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * `HH:mm:ss` 24-hour from picker local time parts (FR-NOTZ-002).
 * FR-AV186-013 — wall-clock midnight coerces to `00:01:00`.
 * `_timeZone` retained for call-site compatibility; not used for reformatting.
 */
export function formatBookingTimeForApi(date: Date, _timeZone?: string): string {
  const hour = date.getHours();
  const minute = date.getMinutes();
  const second = date.getSeconds();
  if (hour === 0 && minute === 0 && second === 0) {
    return "00:01:00";
  }
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
}

export function buildCreateBookingRequest(draft: CreateBookingWizardDraft): CreateBookingRequest {
  const customerId = parseInt(draft.stepOne.customerId, 10);
  const locationId = parseInt(draft.stepOne.locationId, 10);
  const timeZone = resolveCreateBookingTimeZone(draft);
  const bookingType = draft.stepOne.bookingType;

  const request: CreateBookingRequest = {
    customerId,
    locationId,
    bookingType,
  };

  const equipmentPieces = resolveEquipmentPiecesForPayload(draft);
  if (equipmentPieces) {
    request.equipmentPieces = equipmentPieces;
  }

  applyScheduleFieldsToRequest(request, draft, timeZone);

  const dayRate = draft.costPayload?.dayRate;
  if (dayRate != null && Number.isFinite(dayRate) && dayRate >= 0) {
    request.dayRate = dayRate;
  } else {
    request.dayRate = null;
  }

  request.sealCapture = buildSealCapture(resolveSealFields(draft), bookingType);

  if (bookingType === "pickup") {
    request.pickup = buildPickupPayload(draft);
  } else {
    request.dropoff = buildDropoffPayload(draft);
  }

  return request;
}

function applyScheduleFieldsToRequest(
  request: CreateBookingRequest,
  draft: CreateBookingWizardDraft,
  timeZone: string,
): void {
  applySplitScheduleField(
    request,
    "scheduleDate",
    "scheduleTime",
    draft.scheduledDatePart,
    draft.scheduledTimePart,
    draft.scheduledDateTime,
    timeZone,
  );
  applySplitScheduleField(
    request,
    "mustArriveByDate",
    "mustArriveByTime",
    draft.mustArriveDatePart,
    draft.mustArriveTimePart,
    draft.mustArriveBy,
    timeZone,
  );
}

function applySplitScheduleField(
  request: CreateBookingRequest,
  dateKey: "scheduleDate" | "mustArriveByDate",
  timeKey: "scheduleTime" | "mustArriveByTime",
  datePart: Date | null | undefined,
  timePart: Date | null | undefined,
  combined: Date | null | undefined,
  timeZone: string,
): void {
  if (datePart) {
    request[dateKey] = formatBookingDateForApi(datePart, timeZone);
    if (timePart) {
      const combinedParts = combineScheduleDateAndTime(datePart, timePart);
      if (combinedParts) {
        request[timeKey] = formatBookingTimeForApi(combinedParts, timeZone);
      }
    }
    return;
  }
  if (combined) {
    request[dateKey] = formatBookingDateForApi(combined, timeZone);
    request[timeKey] = formatBookingTimeForApi(combined, timeZone);
  }
}

function resolveEquipmentPiecesForPayload(draft: CreateBookingWizardDraft): string | undefined {
  if (draft.stepOne.bookingType === "dropoff" && draft.stepOne.storageType === "wheeled") {
    return resolveEquipmentPiecesWireLabel("one_piece", draft.equipmentPiecesLookup ?? []);
  }

  const slug = draft.stepOne.equipmentPieces;
  if (!slug) return undefined;

  const appliesToFlow =
    (draft.stepOne.bookingType === "dropoff" && draft.stepOne.storageType === "stack") ||
    draft.stepOne.bookingType === "pickup";
  if (!appliesToFlow) return undefined;

  return resolveEquipmentPiecesWireLabel(slug, draft.equipmentPiecesLookup ?? []);
}

function resolveSealFields(draft: CreateBookingWizardDraft): CreateBookingSealCaptureFields | null {
  if (draft.stepOne.bookingType === "pickup") {
    return draft.pickupEquipment ?? null;
  }
  if (draft.stepOne.storageType === "stack") {
    return draft.dropoffStack ?? null;
  }
  return draft.dropoffWheeled ?? null;
}

function buildSealCapture(
  seal: CreateBookingSealCaptureFields | null,
  bookingType: CreateBookingStepOneValues["bookingType"],
): CreateBookingSealCapture {
  if (!seal?.requireSealCapture) {
    return { sealCaptureEnabled: false, selectedSealCapture: "" };
  }
  return {
    sealCaptureEnabled: true,
    selectedSealCapture: bookingType === "pickup" ? "OutGate" : "InGate",
  };
}

function reeferToBool(choice: CreateBookingReeferChoice | null | undefined): boolean | undefined {
  if (choice === "yes") return true;
  if (choice === "no") return false;
  return undefined;
}

function buildPickupPayload(draft: CreateBookingWizardDraft): NonNullable<CreateBookingRequest["pickup"]> {
  if (draft.stepOne.equipmentPieces === "multiple_pieces") {
    return buildPickupMultipiecePayload(draft);
  }
  return buildSinglePiecePickupPayload(draft);
}

function buildSinglePiecePickupPayload(
  draft: CreateBookingWizardDraft,
): NonNullable<CreateBookingRequest["pickup"]> {
  const snapshot = draft.pickupDetailSnapshot;
  const equipment =
    draft.pickupStaticEquipment ?? findPickupStaticEquipmentFromPickupBranch(snapshot)?.equipment ?? null;
  const equipmentDetails = resolvePickupEquipmentDetailsLabel(draft, equipment, snapshot);
  const storageType = resolvePickupPayloadStorageType(equipment, snapshot);

  if (storageType === "stack") {
    return buildPickupStackBranchPayload(equipmentDetails, equipment, snapshot);
  }
  return buildPickupWheeledBranchPayload(equipmentDetails, equipment, snapshot);
}

function resolvePickupEquipmentDetailsLabel(
  draft: CreateBookingWizardDraft,
  equipment: PickupStaticEquipment | null,
  snapshot: BookingDetailPickup | null | undefined,
): string {
  return (
    equipment?.equipmentDetails?.trim() ||
    draft.pickupEquipmentDetails?.trim() ||
    snapshot?.equipmentDetails?.trim() ||
    draft.pickupEquipmentLabel?.trim() ||
    equipment?.equipmentName?.trim() ||
    ""
  );
}

function buildPickupStackBranchPayload(
  equipmentDetails: string,
  equipment: PickupStaticEquipment | null,
  snapshot: BookingDetailPickup | null | undefined,
): NonNullable<CreateBookingRequest["pickup"]> {
  const stackSource = equipment ? buildPickupStackFromStaticEquipment(equipment) : snapshot?.stack;
  const pickup: NonNullable<CreateBookingRequest["pickup"]> = {
    storageType: "stack",
  };
  if (equipmentDetails) {
    pickup.equipmentDetails = equipmentDetails;
  }
  if (stackSource) {
    pickup.stack = {
      isContainerReefer: stackSource.isContainerReefer ?? false,
      containerNumber: stackSource.containerNumber ?? "",
      containerSize: stackSource.containerSize?.trim() || undefined,
      equipmentOwner: stackSource.equipmentOwner?.trim() || undefined,
    };
  }
  return pickup;
}

function buildPickupWheeledBranchPayload(
  equipmentDetails: string,
  equipment: PickupStaticEquipment | null,
  snapshot: BookingDetailPickup | null | undefined,
): NonNullable<CreateBookingRequest["pickup"]> {
  const equipmentVariant = equipment
    ? resolvePickupWheeledVariant(equipment)
    : snapshot?.wheeled?.equipmentVariant ?? "chassis";
  const pickup: NonNullable<CreateBookingRequest["pickup"]> = {
    storageType: "wheeled",
    wheeled: buildPickupWheeledBranch(equipmentVariant, equipment, snapshot?.wheeled),
  };
  if (equipmentDetails) {
    pickup.equipmentDetails = equipmentDetails;
  }
  return pickup;
}

function buildPickupChassisFromStatic(equipment: PickupStaticEquipment): CreateBookingDropoffChassis {
  return {
    chassisId: equipment.chassisId ?? "",
    licensePlate: equipment.licensePlate?.trim() || undefined,
    equipmentOwner: equipment.equipmentOwner?.trim() || undefined,
  };
}

function buildPickupContainerFromStatic(equipment: PickupStaticEquipment): CreateBookingDropoffContainer {
  return {
    isReefer: equipment.isContainerReefer,
    containerNumber: equipment.containerNumber ?? "",
    containerSize: equipment.containerSize?.trim() || undefined,
    equipmentOwner: equipment.equipmentOwner?.trim() || undefined,
  };
}

function buildPickupTrailerFromStatic(equipment: PickupStaticEquipment): CreateBookingDropoffTrailer {
  return {
    isReefer: equipment.isTrailerReefer,
    trailerNumber: equipment.trailerNumber ?? "",
    trailerSize: equipment.trailerSize ?? equipment.equipmentSize ?? "",
    licensePlate: equipment.licensePlate?.trim() || undefined,
    equipmentOwner: equipment.equipmentOwner?.trim() || undefined,
  };
}

type BookingDetailWheeledBranch = NonNullable<BookingDetailPickup["wheeled"]>;

function buildPickupWheeledBranch(
  equipmentVariant: CreateBookingDropoffWheeled["equipmentVariant"],
  equipment: PickupStaticEquipment | null,
  wheeledSource: BookingDetailWheeledBranch | undefined,
): CreateBookingDropoffWheeled {
  if (equipmentVariant === "chassis") {
    return buildWheeledBranchPayload(equipmentVariant, {
      chassis: equipment ? buildPickupChassisFromStatic(equipment) : wheeledSource?.chassis,
    });
  }
  if (equipmentVariant === "chassisAndContainer") {
    return buildWheeledBranchPayload(equipmentVariant, {
      chassis: equipment ? buildPickupChassisFromStatic(equipment) : wheeledSource?.chassis,
      container: equipment ? buildPickupContainerFromStatic(equipment) : wheeledSource?.container,
    });
  }
  return buildWheeledBranchPayload(equipmentVariant, {
    trailer: equipment ? buildPickupTrailerFromStatic(equipment) : wheeledSource?.trailer,
  });
}

function buildWheeledBranchPayload(
  equipmentVariant: CreateBookingDropoffWheeled["equipmentVariant"],
  parts: {
    chassis?: CreateBookingDropoffChassis;
    container?: CreateBookingDropoffContainer;
    trailer?: CreateBookingDropoffTrailer;
  },
): CreateBookingDropoffWheeled {
  const wheeled: CreateBookingDropoffWheeled = { equipmentVariant };
  if (equipmentVariant === "chassis" && parts.chassis) {
    wheeled.chassis = parts.chassis;
  } else if (equipmentVariant === "chassisAndContainer") {
    if (parts.chassis) wheeled.chassis = parts.chassis;
    if (parts.container) wheeled.container = parts.container;
  } else if (equipmentVariant === "trailer" && parts.trailer) {
    wheeled.trailer = parts.trailer;
  }
  return wheeled;
}

function buildDropoffChassisFromForm(
  wheeled: CreateBookingDropoffWheeledEquipmentValues | null | undefined,
): CreateBookingDropoffChassis {
  return {
    chassisId: wheeled?.chassisId ?? "",
    licensePlate: wheeled?.licensePlate?.trim() || undefined,
    equipmentOwner: wheeled?.equipmentOwner?.trim() || undefined,
  };
}

function buildDropoffContainerFromForm(
  wheeled: CreateBookingDropoffWheeledEquipmentValues | null | undefined,
): CreateBookingDropoffContainer {
  return {
    isReefer: reeferToBool(wheeled?.containerIsReefer),
    containerNumber: wheeled?.containerNumber ?? "",
    containerSize: wheeled?.containerSize?.trim() || undefined,
    equipmentOwner: wheeled?.containerEquipmentOwner?.trim() || undefined,
  };
}

function buildDropoffTrailerFromForm(
  wheeled: CreateBookingDropoffWheeledEquipmentValues | null | undefined,
): CreateBookingDropoffTrailer {
  return {
    isReefer: reeferToBool(wheeled?.trailerIsReefer),
    trailerNumber: wheeled?.trailerNumber ?? "",
    trailerSize: wheeled?.trailerSize ?? "",
    licensePlate: wheeled?.trailerLicensePlate?.trim() || undefined,
    equipmentOwner: wheeled?.trailerEquipmentOwner?.trim() || undefined,
  };
}

function buildDropoffWheeledBranch(
  wheeled: CreateBookingDropoffWheeledEquipmentValues | null | undefined,
): CreateBookingDropoffWheeled | null {
  const equipmentVariant = mapWheeledEquipmentVariant(wheeled?.droppedOffEquipment);
  if (!equipmentVariant) return null;
  if (equipmentVariant === "chassis") {
    return buildWheeledBranchPayload(equipmentVariant, {
      chassis: buildDropoffChassisFromForm(wheeled),
    });
  }
  if (equipmentVariant === "chassisAndContainer") {
    return buildWheeledBranchPayload(equipmentVariant, {
      chassis: buildDropoffChassisFromForm(wheeled),
      container: buildDropoffContainerFromForm(wheeled),
    });
  }
  return buildWheeledBranchPayload(equipmentVariant, {
    trailer: buildDropoffTrailerFromForm(wheeled),
  });
}

function buildPickupMultipiecePayload(
  draft: CreateBookingWizardDraft,
): NonNullable<CreateBookingRequest["pickup"]> {
  const pickupEquipment = draft.pickupEquipment;
  const equipmentIds = pickupEquipment?.equipmentIds ?? [];
  const equipmentDetails =
    composePickupMultipieceEquipmentDetailsLabel(equipmentIds) ||
    draft.pickupEquipmentDetails?.trim() ||
    draft.pickupEquipmentLabel?.trim() ||
    "";

  const stack: NonNullable<CreateBookingRequest["pickup"]>["stack"] = {};
  if (pickupEquipment?.quantity != null) {
    stack.containerQuantity = pickupEquipment.quantity;
  }
  if (pickupEquipment?.expiresWhen) {
    stack.expiresWhen = resolveExpiresWhenWireLabel(
      pickupEquipment.expiresWhen,
      draft.expiresWhenLookup ?? [],
    );
  }

  const multiPieceEquipmentDetails = buildPickupMultiPieceEquipmentDetailsFromWizardIds(
    equipmentIds,
    draft.pickupDetailSnapshot,
  );

  const pickup: NonNullable<CreateBookingRequest["pickup"]> = {
    storageType: "stack",
    stack,
  };
  if (equipmentDetails) {
    pickup.equipmentDetails = equipmentDetails;
  }
  if (multiPieceEquipmentDetails.length > 0) {
    pickup.multiPieceEquipmentDetails = multiPieceEquipmentDetails;
  }
  return pickup;
}

function buildPickupMultiPieceEquipmentDetailsFromWizardIds(
  equipmentIds: string[],
  snapshot: BookingDetailPickup | null | undefined,
): ReturnType<typeof buildPickupMultiPieceEquipmentDetails> {
  const fromCatalog = buildPickupMultiPieceEquipmentDetails(
    equipmentIds.filter((id) => !isPickupMultiPieceWireRowOptionId(id)),
  );
  const wireRows = equipmentIds
    .map((id, index) => {
      if (!isPickupMultiPieceWireRowOptionId(id)) return null;
      const wireIndex = Number(id.slice(PICKUP_MULTIPIECE_WIRE_ROW_ID_PREFIX.length));
      const rowIndex = Number.isInteger(wireIndex) ? wireIndex : index;
      const snapshotRow = snapshot?.multiPieceEquipmentDetails?.[rowIndex];
      if (!snapshotRow) return null;
      return {
        isReefer: snapshotRow.isReefer,
        containerNumber: snapshotRow.containerNumber,
        ...(snapshotRow.containerSize ? { containerSize: snapshotRow.containerSize } : {}),
        ...(snapshotRow.equipmentOwner ? { equipmentOwner: snapshotRow.equipmentOwner } : {}),
      };
    })
    .filter((row): row is NonNullable<typeof row> => row != null);

  return [...fromCatalog, ...wireRows];
}

function buildPickupStackFromStaticEquipment(equipment: PickupStaticEquipment): {
  isContainerReefer: boolean;
  containerNumber: string;
  containerSize?: string;
  equipmentOwner?: string;
} {
  return {
    isContainerReefer: equipment.isContainerReefer ?? false,
    containerNumber: equipment.containerNumber ?? "",
    containerSize: equipment.containerSize?.trim() || undefined,
    equipmentOwner: equipment.equipmentOwner?.trim() || undefined,
  };
}

function resolvePickupPayloadStorageType(
  equipment: PickupStaticEquipment | null | undefined,
  snapshot: BookingDetailPickup | null | undefined,
): "stack" | "wheeled" {
  if (equipment?.storageType) {
    return normalizePickupStorageType(equipment.storageType);
  }
  if (snapshot?.storageType === "stack" || snapshot?.storageType === "wheeled") {
    return snapshot.storageType;
  }
  if (snapshot?.wheeled) return "wheeled";
  if (snapshot?.stack) return "stack";
  return "stack";
}

function normalizePickupStorageType(raw: string | null | undefined): "stack" | "wheeled" {
  return String(raw ?? "").trim().toLowerCase() === "wheeled" ? "wheeled" : "stack";
}

function resolvePickupWheeledVariant(
  equipment: PickupStaticEquipment | null | undefined,
): PickupStaticWheeledVariant {
  if (equipment?.wheeledVariant === "trailer") return "trailer";
  if (equipment?.wheeledVariant === "chassis") return "chassis";
  if (equipment?.wheeledVariant === "chassisAndContainer") return "chassisAndContainer";
  if (equipment?.equipmentType.trim().toLowerCase() === "chassis and container")
    return "chassisAndContainer";
  if (equipment?.equipmentType.trim().toLowerCase() === "trailer") return "trailer";
  return "chassis";
}

function buildDropoffPayload(draft: CreateBookingWizardDraft): NonNullable<CreateBookingRequest["dropoff"]> {
  const storageType = draft.stepOne.storageType;
  if (storageType === "stack") {
    return {
      storageType: "stack",
      stack: buildDropoffStackPayload(draft),
    };
  }

  const wheeledBranch = buildDropoffWheeledBranch(draft.dropoffWheeled);
  return {
    storageType: "wheeled",
    ...(wheeledBranch ? { wheeled: wheeledBranch } : {}),
  };
}

function buildDropoffStackPayload(draft: CreateBookingWizardDraft): NonNullable<CreateBookingRequest["dropoff"]>["stack"] {
  const stack = draft.dropoffStack;
  const isMultiple = draft.stepOne.equipmentPieces === "multiple_pieces";
  const stackPayload: NonNullable<NonNullable<CreateBookingRequest["dropoff"]>["stack"]> = {};

  if (isMultiple) {
    if (stack?.quantity != null) {
      stackPayload.containerQuantity = stack.quantity;
    }
    if (stack?.expiresWhen) {
      stackPayload.expiresWhen = resolveExpiresWhenWireLabel(
        stack.expiresWhen,
        draft.expiresWhenLookup ?? [],
      );
    }
  }

  const reefer = reeferToBool(stack?.isReefer);
  if (reefer !== undefined) {
    stackPayload.isContainerReefer = reefer;
  }

  const containerNumber = stack?.containerNumber?.trim();
  if (containerNumber) {
    stackPayload.containerNumber = containerNumber;
  }

  const containerSize = stack?.containerSize?.trim();
  if (containerSize) {
    stackPayload.containerSize = containerSize;
  }

  const equipmentOwner = stack?.equipmentOwner?.trim();
  if (equipmentOwner) {
    stackPayload.equipmentOwner = equipmentOwner;
  }

  return stackPayload;
}

function mapWheeledEquipmentVariant(
  dropped: CreateBookingDropoffWheeledEquipmentValues["droppedOffEquipment"] | undefined,
): "chassis" | "chassisAndContainer" | "trailer" | null {
  if (!dropped) return null;
  if (dropped === "chassis_and_container") return "chassisAndContainer";
  if (dropped === "trailer") return "trailer";
  return "chassis";
}

function resolveBrowserTimeZone(): string {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidTimeZone(browserTimeZone) ? browserTimeZone : "UTC";
}

function isValidTimeZone(timeZone: string | undefined): boolean {
  if (!timeZone) return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}
